"use strict";
// Copyright 2022 Fergal Daly <fergald@gmail.com>.
Object.defineProperty(exports, "__esModule", { value: true });
exports.Linguist = exports.LanguageMap = exports.JA = exports.EN = void 0;
exports.languageFromUrl = languageFromUrl;
exports.languageFromDocument = languageFromDocument;
exports.createLanguageMap = createLanguageMap;
const dates = require("./dates");
const pad = require("./pad");
class LanguageImpl {
    constructor(name) {
        this.name = name;
    }
}
exports.EN = new LanguageImpl('en');
exports.JA = new LanguageImpl('ja');
function languageFromUrl(url_string) {
    const url = new URL(url_string);
    if (url.pathname.startsWith('/ja/')) {
        return exports.JA;
    }
    return exports.EN;
}
function languageFromDocument(document) {
    return languageFromUrl(document.URL);
}
class Message {
    constructor(name) {
        this.name = name;
    }
}
const EN_MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];
const EN_DAYS = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];
const EN_SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const JA_DAYS = ['日', '月', '火', '水', '木', '金', '土'];
class LanguageMap {
    constructor(messageMap) {
        this.maps = messageMap;
        this.languageMap = new Map(messageMap);
    }
    getMessageMapOrThrow(language) {
        const enMessages = this.languageMap.get(language);
        if (!enMessages) {
            throw 'No en messages';
        }
        return enMessages;
    }
    addMessage(id, strings) {
        if (strings.length != this.maps.length) {
            throw `${strings.length} != ${this.maps.length}`;
        }
        for (let i = 0; i < strings.length; i++) {
            this.maps[i][1].set(id, strings[i]);
        }
    }
}
exports.LanguageMap = LanguageMap;
function createLanguageMap(languages, messages) {
    const data = [];
    for (const language of languages) {
        data.push([language, new Map()]);
    }
    const languageMap = new LanguageMap(data);
    for (const message of messages) {
        languageMap.addMessage(message[0], message.slice(1));
    }
    return languageMap;
}
const LANGUAGE_MAP = createLanguageMap([exports.EN, exports.JA], [
    ['day-1', 'Mon', '月'],
    ['day-2', 'Tue', '火'],
    ['day-3', 'Wed', '水'],
    ['day-4', 'Thu', '木'],
    ['day-5', 'Fri', '金'],
    ['day-6', 'Sat', '土'],
    ['day-7', 'Sun', '日'],
]);
class Messages {
    constructor(messages) {
        this.messages = messages;
    }
}
class Linguist {
    constructor(language, messages, linguist) {
        this.language = language;
        this.messages = messages;
        this.linguist = linguist;
    }
    static fromLanguage(language) {
        return new Linguist(language, LANGUAGE_MAP.getMessageMapOrThrow(language));
    }
    static fromDocument() {
        return this.fromLanguage(languageFromDocument(document));
    }
    static fromUrl(url) {
        return this.fromLanguage(languageFromUrl(url));
    }
    getMessages() {
        return this.messages;
    }
    getMessage(id) {
        const trans = this.getMessages().get(id);
        if (trans !== undefined) {
            return trans;
        }
        if (this.linguist !== undefined) {
            return this.linguist.getMessage(id);
        }
        console.log('missing translation', id);
        return id;
    }
    // 1-based.
    getWordyDateNoYear(month, day) {
        switch (this.language) {
            case exports.JA:
                return `${month}月 ${day}日`;
            default:
                return `${day} ${this.getMonth(month)}`;
        }
    }
    getTimeList(times) {
        const timeStrings = new Set();
        for (const time of times) {
            timeStrings.add(time.getHours() * 60 + time.getMinutes());
        }
        return Array.from(timeStrings)
            .sort((a, b) => a - b)
            .map(t => this.getAmPmTime(Math.floor(t / 60), t % 60))
            .join('/');
    }
    getAmPmTime(hours, minutes) {
        const ampm = hours > 12 ? 'PM' : 'AM';
        hours = hours % 12;
        if (hours == 0) {
            hours = 12;
        }
        const minutes_string = minutes == 0 ? '' : `:${pad.n2(minutes)}`;
        return `${hours}${minutes_string}${ampm}`;
    }
    getWordyDateList(times) {
        const lines = [];
        for (const month of dates.groupByMonth(times)) {
            const monthName = this.getMonth(month[0].getMonth());
            const days = Array.from(new Set(month.map(d => d.getDate())))
                .sort()
                .join(', ');
            const timeList = this.getTimeList(month);
            switch (this.language) {
                case exports.JA:
                    lines.push({ dates: `${monthName} ${days}日`, times: timeList });
                    break;
                default:
                    lines.push({ dates: `${monthName} ${days}`, times: timeList });
            }
        }
        return lines;
    }
    // 1-based.
    getWordyYear(year) {
        switch (this.language) {
            case exports.JA:
                return `${year}年`;
            default:
                return `${year}`;
        }
    }
    // 1-based.
    getWordyDate(year, month, day) {
        return this.getWordyYear(year) + ' ' + this.getWordyDateNoYear(month, day);
    }
    // 1-based.
    getMonth(month) {
        switch (this.language) {
            case exports.JA:
                return `${month}月`;
            default:
                return EN_MONTHS[month - 1];
        }
    }
    // 1-based.
    getDayOfMonth(day) {
        switch (this.language) {
            case exports.JA:
                return `${day}日`;
            default:
                return `${day}`;
        }
    }
    // 0 == Sunday.
    getLongDay(day) {
        switch (this.language) {
            case exports.JA:
                return `${JA_DAYS[day]}`;
            default:
                return EN_DAYS[day];
        }
    }
    // 0 == Sunday.
    getShortDay(day) {
        switch (this.language) {
            case exports.JA:
                return `${JA_DAYS[day]}`;
            default:
                return EN_SHORT_DAYS[day];
        }
    }
    getDurationUnits() {
        switch (this.language) {
            case exports.JA:
                return ['時', '分', '間'];
            default:
                return ['h', 'm', ''];
        }
    }
    getShowDurationNoSuffix(minutes, h, m) {
        if (minutes % 60 == 0) {
            return `${minutes / 60}${h}`;
        }
        const hours = Math.floor(minutes / 60);
        const mins = minutes - hours * 60;
        const minString = `${pad.n2(mins)}${m}`;
        if (hours == 0) {
            return minString;
        }
        return `${hours}${h}${minString}`;
    }
    getShowDuration(minutes) {
        const [h, m, suffix] = this.getDurationUnits();
        const suffixless = this.getShowDurationNoSuffix(minutes, h, m);
        return `${suffixless}${suffix}`;
    }
}
exports.Linguist = Linguist;
